import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableContext from './table-context';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import {
  SortingState,
  EditingState,
  PagingState,
  FilteringState,
  IntegratedPaging,
  IntegratedSorting,
  IntegratedFiltering,
  CustomPaging,
} from '@devexpress/dx-react-grid';
import Paper from '@mui/material/Paper';
import {
  Grid,
  Table,
  TableInlineCellEditing,
  TableHeaderRow,
  TableEditRow,
  TableEditColumn,
  PagingPanel,
  DragDropProvider,
  TableColumnReordering,
  TableFixedColumns,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import { copyDeep, getSafe } from '../../../shared/utility';
import * as STATE_PATHS from '../../../shared/const/Routes/statePaths';
import {
  setPageAsClicked,
  resetClickedPagesArray,
} from '../../../store/actions/genericTable/genericTable';
import './GenericTable.scss';
import Command from './Commands';
import Cell from './Cell';
import EditCell from './EditCell';
import EditCommandColumnCell from './EditCommandColumnCell';
import Row from './Row';
import uniqid from 'uniqid';

const getRowId = row => row.id;

const GenericTable = props => {
  const dispatch = useDispatch();
  const clickedPagesInTable = useSelector(state =>
    getSafe(STATE_PATHS.CLICKED_PAGES_ARRAY, state)
  );
  const totalCount = useSelector(state =>
    getSafe(STATE_PATHS.TOTAL_COUNT, state)
  );
  const [columns] = useState(props.columns);
  const [rows, setRows] = useState([]);
  const [columnOrder, setColumnOrder] = useState(props.columnOrder);
  const [tableColumnExtensions] = useState(props.tableColumnExtensions);
  const [filteringStateColumnExtensions] = useState(
    props.filteringStateColumnExtensions
  );
  const [pageSizes] = useState(props.pageSizes);
  const [pageSize, setPageSize] = useState(props.pageSize || 5);
  const [currentPage, setCurrentPage] = useState(0);
  const [tableChanges, setTableChanges] = useState({
    add: {},
    update: {},
    delete: {},
  });
  const [sorting, getSorting] = useState([]);
  const [editingRowIds, getEditingRowIds] = useState([]);
  const [addedRows, setAddedRows] = useState([]);
  const [rowChanges, setRowChanges] = useState({});
  const [leftFixedColumns] = useState([TableEditColumn.COLUMN_TYPE]);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const columnsConfiguration = props.columnsConfiguration;
  const columnsTypeOptionsConfiguration = props.columnsTypeOptionsConfiguration;
  const placeholders = props.placeholders;
  const { selectedRowStatePath, setSelectedRow } = props;

  useEffect(() => {
    return () => {
      dispatch(resetClickedPagesArray());
    };
  }, []);

  useEffect(() => {
    if (!!props.pagingEnabled && !props.isDefaultPaging) {
      updateVisibleRows(currentPage);
      setIsLoading(false);
    } else setRows(props.rows);
  }, [props.rows]);

  useEffect(() => {
    if (!!props.handleTableChanges)
      props.handleTableChanges(tableChanges, props.tableKey, currentRowId);
  }, [JSON.parse(JSON.stringify(tableChanges))]);

  useEffect(() => {
    setTableChanges({ add: {}, update: {}, delete: {} });
  }, [props.tableTitle]);

  const updateVisibleRows = page => {
    const indexOfPage = clickedPagesInTable.sort().indexOf(page);
    const startIndex = indexOfPage * pageSize;
    const endPage = indexOfPage * pageSize + pageSize;
    setRows(props.rows.slice(startIndex, endPage));
  };

  const onCurrentPageChange = page => {
    if (!props.isDefaultPaging) {
      setIsLoading(true);
      if (!clickedPagesInTable.includes(page))
        props.onPagingChange(page, pageSize);
      else {
        updateVisibleRows(page);
        setIsLoading(false);
      }
      dispatch(setPageAsClicked(page));
    }
    setCurrentPage(page);
  };

  const onPageSizeChange = size => {
    if (!props.isDefaultPaging) {
      setIsLoading(true);
      dispatch(resetClickedPagesArray());
      props.onPageSizeChange(0, size);
      setCurrentPage(0);
    }
    setPageSize(size);
  };

  const handleAffectedColChanges = params => {
    let changesObjArray = [];
    if (!!columnsConfiguration) {
      const conditionPerCol = columnsConfiguration[params.currentColumnName];
      if (conditionPerCol) {
        const colValCondition = conditionPerCol[params.newColumnValue];
        if (colValCondition) {
          colValCondition.affectedColTargets.forEach(affectedColTargetObj => {
            const affectedColName = affectedColTargetObj.columnName;
            const colConfigValue = affectedColTargetObj.columnConfig.value;
            const changeArgs = {
              rowId: params.editRowId,
              change: params.createRowChange(
                params.row,
                colConfigValue,
                affectedColName
              ),
            };
            changesObjArray.push(changeArgs);
          });
        }
      }
    }
    return changesObjArray;
  };

  const columnNameColumnValueExist = (configurationObj, colName, colValue) => {
    return configurationObj[colName] && configurationObj[colName][colValue];
  };

  const setColTypeAndEditingState = params => {
    const defaultResultObj = {
      colType: params.colType,
      disabled: !params.editingEnabledCell,
      availableColumnValues: params.availableColumnValues,
    };
    let resultObj = defaultResultObj;

    if (!!columnsConfiguration) {
      for (const colName of Object.keys(params.currentRow)) {
        const colValue = params.currentRow[colName];
        if (
          columnNameColumnValueExist(columnsConfiguration, colName, colValue)
        ) {
          const affectedColTargets =
            columnsConfiguration[colName][colValue].affectedColTargets;
          if (affectedColTargets) {
            const foundCurrentColumnNameObj = affectedColTargets.find(
              affectedColTargetObj =>
                affectedColTargetObj.columnName === params.currentColumnName
            );
            if (foundCurrentColumnNameObj) {
              const foundCurrentColumnNameConfig =
                foundCurrentColumnNameObj.columnConfig;
              const type = foundCurrentColumnNameConfig.type;
              const disable =
                foundCurrentColumnNameConfig.disable ||
                !params.editingEnabledCell;
              const options = foundCurrentColumnNameConfig.options;
              resultObj = {
                colType: type,
                disabled: disable,
                availableColumnValues: options,
              };
            }
          }
        }
      }
    } else if (!!columnsTypeOptionsConfiguration) {
      const specificConfigByRowId =
        columnsTypeOptionsConfiguration[params.rowId];
      if (!!specificConfigByRowId) {
        const type = specificConfigByRowId.type;
        const disable = !params.editingEnabledCell;
        const options = specificConfigByRowId.options;
        resultObj = {
          colType: type,
          disabled: disable,
          availableColumnValues: options,
        };
      }
    }

    return resultObj;
  };

  const setPlaceholder = params => {
    let placeholder = params.defaultPlaceholder;

    if (!!placeholders) {
      for (const colName of Object.keys(params.currentRow)) {
        const colValue = params.currentRow[colName];
        if (columnNameColumnValueExist(placeholders, colName, colValue)) {
          const affectedColTargets =
            placeholders[colName][colValue].affectedColTargets;
          if (affectedColTargets) {
            const foundCurrentColumnNameObj = affectedColTargets.find(
              affectedColTargetObj =>
                affectedColTargetObj.columnName === params.currentColumnName
            );
            if (foundCurrentColumnNameObj) {
              placeholder = foundCurrentColumnNameObj.placeholder;
              break;
            }
          }
        }
      }
    }

    return placeholder;
  };

  const deleteRows = deletedIds => {
    const cloneTableRows = copyDeep(rows);
    deletedIds.forEach(rowId => {
      const index = cloneTableRows.findIndex(row => row.id === rowId);
      if (index > -1) {
        const removeId = cloneTableRows[index].id;
        if (props.isAbsolutelyDelete) {
          cloneTableRows.splice(index, 1);
        } else {
          if (!!rows[index].isNew) {
            setCurrentRowId(rowId);

            cloneTableRows.splice(index, 1);
            let cloneAdded = { ...tableChanges.add };
            delete cloneAdded[removeId];
            setTableChanges({ ...tableChanges, add: cloneAdded });
          } else {
            let ourRow = cloneTableRows[index];
            ourRow.isDeleted = true;

            if (tableChanges.update[removeId]) {
              let cloneUpdated = { ...tableChanges.update };
              delete cloneUpdated[removeId];
              setTableChanges({
                ...tableChanges,
                update: cloneUpdated,
                delete: { ...tableChanges.delete, [removeId]: ourRow },
              });
            } else {
              setTableChanges({
                ...tableChanges,
                delete: { ...tableChanges.delete, [removeId]: ourRow },
              });
            }
          }
        }
      }
    });
    return cloneTableRows;
  };

  const commitChanges = ({ added, changed, deleted }) => {
    let changedRows = [...rows];
    if (added) {
      let rowForAdd = { ...added[0], isNew: true, id: uniqid() };
      changedRows = [rowForAdd, ...rows];
      setCurrentRowId(rowForAdd.id);
      setTableChanges({
        ...tableChanges,
        add: { ...tableChanges.add, [rowForAdd.id]: rowForAdd },
      });
    }
    if (changed) {
      changedRows = copyDeep(rows).map(row => {
        if (!!changed[row.id]) {
          setCurrentRowId(row.id);

          if (row.isNew) {
            let cloneTableAdd = { ...tableChanges.add };
            for (const key in changed[row.id]) {
              cloneTableAdd[row.id][key] = changed[row.id][key];
            }
            setTableChanges({ ...tableChanges, add: cloneTableAdd });
          } else {
            let cloneTableChanges = { ...tableChanges.update };
            if (!tableChanges.update.hasOwnProperty([row.id])) {
              cloneTableChanges[row.id] = row;
            }
            for (const key in changed[row.id]) {
              cloneTableChanges[row.id][key] = changed[row.id][key];
            }
            setTableChanges({ ...tableChanges, update: cloneTableChanges });
          }
          return { ...row, ...changed[row.id] };
        } else {
          return row;
        }
      });
    }

    if (deleted) {
      changedRows = deleteRows(deleted);
    }

    setRows(changedRows);

    if (!!props.setRows) {
      let setObj = {};

      if (!!added) {
        setObj = { type: 'add', value: changedRows[0] };
      } else if (!!changed) {
        setObj = {
          type: 'change',
          value: changedRows.find(row => row.id === Object.keys(changed)[0]),
        };
      } else if (!!deleted) {
        setObj = { type: 'delete', value: deleted[0] };
      }

      props.setRows(props.tableKey, changedRows, setObj);
    }
  };

  const changeAddedRows = value => {
    return setAddedRows(
      value.map(row =>
        Object.keys(row).length ? row : { ...props.newEmptyRowObj }
      )
    );
  };
  // TODO: when edit table is TableInlineCellEditing the adding new row doesn't work
  // const addEmptyRow = () => commitChanges({ added: [{}] });
  return (
    <TableContext.Provider
      value={{
        handleAffectedColChanges: handleAffectedColChanges,
        setColTypeAndEditingState: setColTypeAndEditingState,
        setPlaceholder: setPlaceholder,
      }}
    >
      {!!props.getActionContent && props.getActionContent()}
      {!!props.errorMessage && <h4 className="error">{props.errorMessage}</h4>}
      {!!props.tableTitle && (
        <div className="sub-header">{props.tableTitle}</div>
      )}

      <Paper
        className={`table flex-row table-additional-properties ${props.className}`}
        sx={{
          width: '100%',
          overflow: 'hidden',
          Height: '100%',
          display: 'grid',
        }}
      >
        <Grid
          className="grid-generic-table"
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          sx={{ maxHeight: 440 }}
        >
          {props.filteringEnabled && (
            <FilteringState
              defaultFilters={[]}
              columnExtensions={filteringStateColumnExtensions}
            />
          )}
          {props.sortingEnabled && (
            <SortingState sorting={sorting} onSortingChange={getSorting} />
          )}
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={onCurrentPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />

          <EditingState
            columnExtensions={tableColumnExtensions}
            onCommitChanges={commitChanges}
            editingRowIds={editingRowIds}
            onEditingRowIdsChange={getEditingRowIds}
            rowChanges={rowChanges}
            onRowChangesChange={setRowChanges}
            addedRows={addedRows}
            onAddedRowsChange={changeAddedRows}
            // onAddedRowsChange={addEmptyRow} // TODO: when edit table is TableInlineCellEditing the adding new row doesn't work
          />
          {props.filteringEnabled && <IntegratedFiltering />}
          {props.sortingEnabled && <IntegratedSorting />}
          {props.pagingEnabled && props.isDefaultPaging && <IntegratedPaging />}
          {columnOrder && <DragDropProvider />}

          <>
            {isLoading && (
              <div className="loading-container">
                <div className="center-loading flex-1">
                  <LoadingSpinner text={'Loading...'} />
                </div>
              </div>
            )}
            <Table
              columnExtensions={tableColumnExtensions}
              cellComponent={Cell}
              rowComponent={properties =>
                Row({
                  ...properties,
                  selectedRowStatePath: selectedRowStatePath,
                  setSelectedRow: setSelectedRow,
                })
              }
              stickyHeader
              aria-label="sticky table"
            />
          </>

          <TableHeaderRow showSortingControls={props.sortingEnabled} />
          {props.filteringEnabled && <TableFilterRow />}
          <TableColumnReordering
            order={columnOrder}
            onOrderChange={setColumnOrder}
          />
          {props.enableTableEditRow && (
            <TableEditRow cellComponent={EditCell} />
          )}
          {props.enableTableEditRow && (
            <TableEditColumn
              width="7rem"
              showEditCommand={
                !!props.showCommands ? props.showCommands.edit : true
              }
              showAddCommand={
                !!props.showCommands
                  ? props.showCommands.add
                  : !addedRows.length
              }
              showDeleteCommand={
                !!props.showCommands ? props.showCommands.delete : true
              }
              cellComponent={EditCommandColumnCell}
              commandComponent={Command}
            />
          )}
          {props.enableTableInlineCellEditing && (
            <TableInlineCellEditing cellComponent={EditCell} />
          )}
          {props.enableTableInlineCellEditing && (
            <TableEditColumn
              width="7rem"
              showAddCommand
              showDeleteCommand
              commandComponent={Command}
            />
          )}
          <TableFixedColumns leftColumns={leftFixedColumns} />

          {props.pagingEnabled && !props.isDefaultPaging && (
            <CustomPaging totalCount={totalCount} />
          )}
          {props.pagingEnabled && <PagingPanel pageSizes={pageSizes} />}
        </Grid>
      </Paper>
    </TableContext.Provider>
  );
};

export default React.memo(GenericTable);
